import type { CoreAssistantMessage, CoreToolMessage, UIMessage } from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Document } from '@/lib/db/schema';
import { ChatSDKError, type ErrorCode, type ErrorType, type Surface } from './errors';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const fetcher = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    const { code, cause } = await response.json();
    throw new ChatSDKError(code as ErrorCode, cause);
  }

  return response.json();
};

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit,
) {
  try {
    const response = await fetch(input, { ...init, credentials: "include" });

    if (!response.ok) {
      // Map HTTP status codes to error types
      let errorType: ErrorType;
      switch (response.status) {
        case 400:
          errorType = 'bad_request';
          break;
        case 401:
          errorType = 'unauthorized';
          break;
        case 403:
          errorType = 'forbidden';
          break;
        case 404:
          errorType = 'not_found';
          break;
        case 429:
          errorType = 'rate_limit';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorType = 'offline';
          break;
        default:
          errorType = 'bad_request';
      }

      // Extract error message from response body
      let errorMessage: string;
      try {
        const responseBody = await response.json();
        // Handle different API response formats
        errorMessage = responseBody.detail || responseBody.message || responseBody.error || 'Unknown error';
      } catch {
        // If response body is not JSON, use status text
        errorMessage = response.statusText || 'Unknown error';
      }

      // Create error code based on the endpoint being called
      const url = typeof input === 'string' ? input : input.toString();
      const surface: Surface = url.includes('/agent/stream') ? 'stream' : 'api';
      const errorCode: ErrorCode = `${errorType}:${surface}`;

      throw new ChatSDKError(errorCode, errorMessage);
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new ChatSDKError('offline:chat');
    }

    throw error;
  }
}

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function getMostRecentUserMessage(messages: Array<UIMessage>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function getTrailingMessageId({
  messages,
}: {
  messages: Array<ResponseMessage>;
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) return null;

  return trailingMessage.id;
}

export function sanitizeText(text: string) {
  return text.replace('<has_function_call>', '');
}

/**
 * Parse tool result content using existing patterns from DocumentPreview
 */
export function parseToolResult(content: string, toolCallId: string, index: number) {
  try {
    // Try to parse as JSON first (for structured tool results)
    const parsed = JSON.parse(content);
    return parsed;
  } catch {
    // If JSON parsing fails, create expected structure using DocumentPreview patterns
    return {
      execution_result: {
        cell_info: {
          id: toolCallId,
          index: index,
          cell_type: 'code',
          purpose: 'Tool execution result',
          content: content,
          outputs: [{
            output_type: 'stream',
            text: content
          }]
        }
      },
      status: 'success'
    };
  }
}
